import { resolveProjectMention, type ProjectRef } from '@/lib/board-route';
import { isExploreMoreTurn, isFocusedSearchPivot, isSearchRefinementUserLine } from '@/lib/search-refine';

/** Buyer wants the matches hub — clear project focus and show the list. */
export { isExploreMoreTurn } from '@/lib/search-refine';

/** Turns that apply to the whole match set — do not pin engine focus to one project. */
export function isGlobalAdvisorTurn(text: string): boolean {
  // Closed cues only. Open hub phrasing ("which location is better?") is owned by
  // Spine SIL → compare; SPA clears sticky pack from compareMatrix / visit phase.
  if (/\bcompare\b/i.test(text)) return true;
  if (
    /\b(?:which (?:one )?fits|better for my budget|which is better|recommend between)\b/i.test(text)
  ) {
    return true;
  }
  if (isExploreMoreTurn(text)) return true;
  if (isFocusedSearchPivot(text)) return true;
  if (isSearchRefinementUserLine(text)) return true;
  if (/\b(?:plan (?:a )?visit|visit day|site visit|plan visits)\b/i.test(text)) return true;
  if (/\b(?:would like to|want to|like to)\s+(?:to\s+)?visit\b/i.test(text)) return true;
  // Visit itinerary — board may still show a pack; strip project_id for scheduling.
  if (/\b(?:same\s+day|force\s+all|next\s+day|different\s+day|split)\b/i.test(text)) return true;
  if (/\b(?:start(?:ing)?|coming|leave)\s+from\b/i.test(text)) return true;
  if (/\b(?:add|include|also|too|as well)\b/i.test(text)) return true;
  if (/\bwhat about\b/i.test(text)) return true;
  return false;
}

/** All catalog projects named in free text (for multi-stop visit routing). */
export function resolveMultiProjectMentions(
  text: string,
  projects: readonly ProjectRef[],
): ProjectRef[] {
  const lc = text.toLowerCase();
  const found: ProjectRef[] = [];
  for (const p of projects) {
    const name = p.name.toLowerCase();
    if (lc.includes(name)) {
      if (!found.some((f) => f.id === p.id)) found.push(p);
      continue;
    }
    const parts = p.name.toLowerCase().split(/\s+/).filter((w) => w.length >= 5);
    for (const part of parts) {
      if (lc.includes(part)) {
        if (!found.some((f) => f.id === p.id)) found.push(p);
        break;
      }
    }
  }
  return found;
}

export interface TurnFocus {
  projectId: string;
  projectName: string;
}

/** Sticky focus: named project in text wins, else keep current focus for follow-up questions. */
export function resolveTurnFocus(
  text: string,
  projects: readonly ProjectRef[],
  stickyProjectId: string | null | undefined,
): TurnFocus | undefined {
  if (isGlobalAdvisorTurn(text)) return undefined;

  const mention = resolveProjectMention(text, projects);
  if (mention) return { projectId: mention.id, projectName: mention.name };

  if (stickyProjectId) {
    const sticky = projects.find((p) => p.id === stickyProjectId);
    if (sticky) return { projectId: sticky.id, projectName: sticky.name };
  }

  return undefined;
}

export function projectRefsFromCatalog(
  projects: readonly { id: string; name: string }[],
): ProjectRef[] {
  return projects.map((p) => ({ id: p.id, name: p.name }));
}
